import _ from 'lodash';
import produce from 'immer';
import create from 'zustand';
import moment from 'moment-timezone';

const getComparable = (store, daysAgo, contributors) => {
  const dStore = _.get(store, daysAgo, []);
  return contributors.reduce((result, c) => {
    result[c.id] = Number.isFinite(daysAgo)
      ? _.get(_.find(dStore, { id: c.id }), 'rank', Number.NaN)
      : Number.NaN;
    return result;
  }, {});
};

const getCurrent = (store, contributors) => {
  const dStore = _.get(store, Infinity, []);
  const data = contributors
    .filter(c => c.selected)
    .map(c => ({
      ...c,
      rank: _.get(_.find(dStore, { id: c.id }), 'rank', Number.NaN),
    }));
  return _.sortBy(data, ['rank', 'id']);
};

const useLeaderboardStore = create(set => ({
  requestData: false,
  loading: false,
  daysAgo: Infinity,
  generatedAt: moment(),
  contributors: [],
  /**
   * `current` is an array sorted with 'rank', and then 'id', with each element
   * as:
   *  {
   *    id: PropTypes.number,                 // contributor id
   *    name: PropTypes.string,               // contributor name
   *    profilePictureUrl: PropTypes.string,  // contributor profile picture
   *    company: PropTypes.shape({
   *      name: PropTypes.string,             // company name
   *      logo: PropTypes.string,             // company logo
   *    }),
   *  }
   */
  current: [],
  /**
   * `comparable` is a hash of all the contributors' historical ranking for the
   * current selected date range, with:
   *    key: the contributor's id
   *    value: the contributor's ranking
   */
  comparable: {},
  rankingsStore: {},
  set: fn => set(produce(fn)),
  setRankingStore: d => set(produce(state => {
    state.rankingStore = {
      ...state.rankingStore,
      [d.date]: d.data,
    };
    if (d.date === Infinity) {
      state.current = getCurrent(state.rankingStore, state.contributors);
    }
    if (d.date === state.daysAgo) {
      state.comparable = getComparable(state.rankingStore, state.daysAgo, state.contributors);
    }
  })),
  setDaysAgo: d => set(state => ({
    daysAgo: d,
    comparable: getComparable(state.rankingStore, d, state.contributors),
  })),
  setContributors: d => set(state => ({
    contributors: d || [],
    current: getCurrent(state.rankingStore, d || []),
    comparable: getComparable(state.rankingStore, state.daysAgo, d || []),
  })),
}));

export default useLeaderboardStore;
